import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { contaApi } from '../../services/api';
import { useClienteAuth } from '../../contexts/ClienteAuthContext';
import { LogoMark, Spinner } from '../../components/ui';
import {
  ArrowLeft, Eye, EyeOff, MailCheck, ShieldCheck, KeyRound, User, Phone, Mail, Lock,
} from 'lucide-react';

type Etapa = 'login' | 'cadastro' | 'verificar' | 'esqueci' | 'redefinir';

function CampoSenha({
  id, rotulo, valor, onChange, placeholder = '••••••••', autoComplete,
}: {
  id: string; rotulo: string; valor: string; onChange: (v: string) => void;
  placeholder?: string; autoComplete?: string;
}) {
  const [mostrar, setMostrar] = useState(false);
  return (
    <div>
      <label className="input-label" htmlFor={id}>{rotulo}</label>
      <div className="relative">
        <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
        <input
          id={id}
          type={mostrar ? 'text' : 'password'}
          value={valor}
          onChange={e => onChange(e.target.value)}
          placeholder={placeholder}
          className="input-field !pl-10 pr-11"
          autoComplete={autoComplete}
          required
        />
        <button
          type="button"
          onClick={() => setMostrar(!mostrar)}
          className="absolute right-1.5 top-1/2 -translate-y-1/2 icon-btn"
          aria-label={mostrar ? 'Ocultar senha' : 'Mostrar senha'}
        >
          {mostrar ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
}

export function ContaLoginPage() {
  const navigate = useNavigate();
  const { login, definirSessao } = useClienteAuth();

  const [etapa, setEtapa] = useState<Etapa>('login');
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [codigo, setCodigo] = useState('');
  const [devCodigo, setDevCodigo] = useState('');
  const [erro, setErro] = useState('');
  const [aviso, setAviso] = useState('');
  const [carregando, setCarregando] = useState(false);

  const limpar = () => { setErro(''); setAviso(''); };

  const irPara = (e: Etapa) => { limpar(); setCodigo(''); setDevCodigo(''); setEtapa(e); };

  const erroMsg = (err: any, padrao: string) =>
    err.response?.data?.error || padrao;

  // ── Login ──────────────────────────────────────────────
  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    limpar();
    setCarregando(true);
    try {
      await login(email.trim(), senha);
      navigate('/loja/conta');
    } catch (err: any) {
      if (err.response?.data?.codigo === 'EMAIL_NAO_VERIFICADO') {
        setAviso('Seu e-mail ainda não foi verificado. Enviamos um novo código.');
        try {
          const r = await contaApi.reenviarCodigo(email.trim());
          if (r.data.devCodigo) setDevCodigo(r.data.devCodigo);
        } catch { /* silencioso */ }
        setEtapa('verificar');
      } else {
        setErro(erroMsg(err, 'Erro ao entrar. Verifique suas credenciais.'));
      }
    } finally {
      setCarregando(false);
    }
  };

  // ── Cadastro ───────────────────────────────────────────
  const handleCadastro = async (e: React.FormEvent) => {
    e.preventDefault();
    limpar();
    setCarregando(true);
    try {
      const res = await contaApi.registro({
        nome: nome.trim(),
        email: email.trim(),
        telefone: telefone.replace(/\D/g, ''),
        senha,
      });
      setAviso(res.data.mensagem);
      if (res.data.devCodigo) setDevCodigo(res.data.devCodigo);
      setEtapa('verificar');
    } catch (err: any) {
      setErro(erroMsg(err, 'Não foi possível criar sua conta.'));
    } finally {
      setCarregando(false);
    }
  };

  // ── Verificação de e-mail ──────────────────────────────
  const handleVerificar = async (e: React.FormEvent) => {
    e.preventDefault();
    limpar();
    setCarregando(true);
    try {
      const res = await contaApi.verificarEmail(email.trim(), codigo.trim());
      definirSessao(res.data.token, res.data.cliente);
      navigate('/loja/conta');
    } catch (err: any) {
      setErro(erroMsg(err, 'Código inválido.'));
    } finally {
      setCarregando(false);
    }
  };

  const handleReenviar = async () => {
    limpar();
    setCarregando(true);
    try {
      const res = await contaApi.reenviarCodigo(email.trim());
      setAviso(res.data.mensagem);
      if (res.data.devCodigo) setDevCodigo(res.data.devCodigo);
    } catch (err: any) {
      setErro(erroMsg(err, 'Não foi possível reenviar o código.'));
    } finally {
      setCarregando(false);
    }
  };

  // ── Esqueci / redefinir senha ──────────────────────────
  const handleEsqueci = async (e: React.FormEvent) => {
    e.preventDefault();
    limpar();
    setCarregando(true);
    try {
      const res = await contaApi.esqueciSenha(email.trim());
      setAviso(res.data.mensagem);
      if (res.data.devCodigo) setDevCodigo(res.data.devCodigo);
      setEtapa('redefinir');
    } catch (err: any) {
      setErro(erroMsg(err, 'Não foi possível enviar o código.'));
    } finally {
      setCarregando(false);
    }
  };

  const handleRedefinir = async (e: React.FormEvent) => {
    e.preventDefault();
    limpar();
    setCarregando(true);
    try {
      const res = await contaApi.redefinirSenha(email.trim(), codigo.trim(), novaSenha);
      definirSessao(res.data.token, res.data.cliente);
      navigate('/loja/conta');
    } catch (err: any) {
      setErro(erroMsg(err, 'Não foi possível redefinir a senha.'));
    } finally {
      setCarregando(false);
    }
  };

  const titulos: Record<Etapa, { titulo: string; subtitulo: string }> = {
    login: { titulo: 'Entrar na sua conta', subtitulo: 'Acompanhe pedidos, pague online e compre mais rápido.' },
    cadastro: { titulo: 'Criar sua conta', subtitulo: 'Leva menos de um minuto.' },
    verificar: { titulo: 'Verifique seu e-mail', subtitulo: `Enviamos um código de 6 dígitos para ${email || 'seu e-mail'}.` },
    esqueci: { titulo: 'Esqueci minha senha', subtitulo: 'Informe seu e-mail para receber o código de redefinição.' },
    redefinir: { titulo: 'Definir nova senha', subtitulo: `Digite o código enviado para ${email || 'seu e-mail'} e a nova senha.` },
  };

  return (
    <div className="min-h-screen bg-ink-50">
      {/* Header */}
      <header className="bg-white border-b border-ink-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/loja" className="flex items-center gap-2.5">
            <LogoMark size={30} />
            <span className="font-display font-bold text-ink-950 tracking-tight">MONCHERY</span>
          </Link>
          <Link
            to="/loja"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-ink-500 hover:text-ink-800 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" /> Voltar à loja
          </Link>
        </div>
      </header>

      <div className="max-w-md mx-auto px-4 sm:px-6 py-12">
        <div className="card animate-fade-up">
          {/* Ícone + título */}
          <div className="flex flex-col items-center text-center mb-7">
            <div className="w-12 h-12 rounded-xl bg-brand-50 flex items-center justify-center mb-4">
              {etapa === 'verificar' || etapa === 'redefinir' ? (
                <MailCheck className="w-6 h-6 text-brand-600" />
              ) : etapa === 'esqueci' ? (
                <KeyRound className="w-6 h-6 text-brand-600" />
              ) : (
                <User className="w-6 h-6 text-brand-600" />
              )}
            </div>
            <h1 className="font-display text-xl font-bold text-ink-950 tracking-tight">
              {titulos[etapa].titulo}
            </h1>
            <p className="text-sm text-ink-500 mt-1.5">{titulos[etapa].subtitulo}</p>
          </div>

          {/* ── LOGIN ── */}
          {etapa === 'login' && (
            <form onSubmit={handleLogin} className="space-y-4">
              <div>
                <label className="input-label" htmlFor="cl-email">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="cl-email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="voce@email.com" className="input-field !pl-10"
                    autoComplete="email" required
                  />
                </div>
              </div>
              <CampoSenha id="cl-senha" rotulo="Senha" valor={senha} onChange={setSenha} autoComplete="current-password" />
              <div className="text-right">
                <button
                  type="button" onClick={() => irPara('esqueci')}
                  className="text-[13px] font-semibold text-brand-600 hover:text-brand-700"
                >
                  Esqueci minha senha
                </button>
              </div>
              <Feedback erro={erro} aviso={aviso} />
              <button type="submit" disabled={carregando} className="btn-primary w-full !py-3">
                {carregando ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Entrar'}
              </button>
              <p className="text-center text-sm text-ink-500">
                Ainda não tem conta?{' '}
                <button type="button" onClick={() => irPara('cadastro')} className="font-semibold text-brand-600 hover:text-brand-700">
                  Cadastre-se
                </button>
              </p>
            </form>
          )}

          {/* ── CADASTRO ── */}
          {etapa === 'cadastro' && (
            <form onSubmit={handleCadastro} className="space-y-4">
              <div>
                <label className="input-label" htmlFor="cc-nome">Nome completo</label>
                <div className="relative">
                  <User className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="cc-nome" type="text" value={nome}
                    onChange={e => setNome(e.target.value)}
                    placeholder="Seu nome" className="input-field !pl-10"
                    autoComplete="name" required
                  />
                </div>
              </div>
              <div>
                <label className="input-label" htmlFor="cc-email">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="cc-email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="voce@email.com" className="input-field !pl-10"
                    autoComplete="email" required
                  />
                </div>
              </div>
              <div>
                <label className="input-label" htmlFor="cc-tel">Telefone / WhatsApp</label>
                <div className="relative">
                  <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="cc-tel" type="tel" value={telefone}
                    onChange={e => setTelefone(e.target.value)}
                    placeholder="(44) 99999-9999" className="input-field !pl-10"
                    autoComplete="tel" required
                  />
                </div>
              </div>
              <CampoSenha id="cc-senha" rotulo="Senha (mín. 6 caracteres)" valor={senha} onChange={setSenha} autoComplete="new-password" />
              <Feedback erro={erro} aviso={aviso} />
              <button type="submit" disabled={carregando} className="btn-primary w-full !py-3">
                {carregando ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Criar conta'}
              </button>
              <p className="text-center text-sm text-ink-500">
                Já tem conta?{' '}
                <button type="button" onClick={() => irPara('login')} className="font-semibold text-brand-600 hover:text-brand-700">
                  Entrar
                </button>
              </p>
            </form>
          )}

          {/* ── VERIFICAR E-MAIL ── */}
          {etapa === 'verificar' && (
            <form onSubmit={handleVerificar} className="space-y-4">
              <CampoCodigo valor={codigo} onChange={setCodigo} />
              {devCodigo && (
                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-[13px] font-medium text-amber-800 animate-fade-in">
                  Modo desenvolvimento (Resend não configurado): seu código é <strong className="tracking-widest">{devCodigo}</strong>
                </div>
              )}
              <Feedback erro={erro} aviso={aviso} />
              <button type="submit" disabled={carregando || codigo.trim().length !== 6} className="btn-primary w-full !py-3">
                {carregando ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Confirmar e-mail'}
              </button>
              <p className="text-center text-sm text-ink-500">
                Não recebeu?{' '}
                <button type="button" onClick={handleReenviar} disabled={carregando} className="font-semibold text-brand-600 hover:text-brand-700">
                  Reenviar código
                </button>
              </p>
              <p className="text-center">
                <button type="button" onClick={() => irPara('login')} className="text-[13px] font-semibold text-ink-400 hover:text-ink-600">
                  Voltar ao login
                </button>
              </p>
            </form>
          )}

          {/* ── ESQUECI A SENHA ── */}
          {etapa === 'esqueci' && (
            <form onSubmit={handleEsqueci} className="space-y-4">
              <div>
                <label className="input-label" htmlFor="ce-email">E-mail cadastrado</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-400 pointer-events-none" />
                  <input
                    id="ce-email" type="email" value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="voce@email.com" className="input-field !pl-10"
                    autoComplete="email" required
                  />
                </div>
              </div>
              <Feedback erro={erro} aviso={aviso} />
              <button type="submit" disabled={carregando} className="btn-primary w-full !py-3">
                {carregando ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Enviar código'}
              </button>
              <p className="text-center">
                <button type="button" onClick={() => irPara('login')} className="text-[13px] font-semibold text-ink-400 hover:text-ink-600">
                  Voltar ao login
                </button>
              </p>
            </form>
          )}

          {/* ── REDEFINIR SENHA ── */}
          {etapa === 'redefinir' && (
            <form onSubmit={handleRedefinir} className="space-y-4">
              <CampoCodigo valor={codigo} onChange={setCodigo} />
              {devCodigo && (
                <div className="p-3.5 bg-amber-50 border border-amber-200 rounded-xl text-[13px] font-medium text-amber-800 animate-fade-in">
                  Modo desenvolvimento (Resend não configurado): seu código é <strong className="tracking-widest">{devCodigo}</strong>
                </div>
              )}
              <CampoSenha id="cr-senha" rotulo="Nova senha (mín. 6 caracteres)" valor={novaSenha} onChange={setNovaSenha} autoComplete="new-password" />
              <Feedback erro={erro} aviso={aviso} />
              <button type="submit" disabled={carregando || codigo.trim().length !== 6} className="btn-primary w-full !py-3">
                {carregando ? <Spinner className="h-5 w-5 !border-white/30 !border-t-white" /> : 'Redefinir senha e entrar'}
              </button>
              <p className="text-center">
                <button type="button" onClick={() => irPara('login')} className="text-[13px] font-semibold text-ink-400 hover:text-ink-600">
                  Voltar ao login
                </button>
              </p>
            </form>
          )}

          <div className="mt-8 pt-5 border-t border-ink-100 flex items-center justify-center gap-2 text-2xs text-ink-400">
            <ShieldCheck className="w-4 h-4 text-ink-300" />
            Seus dados são protegidos e usados apenas nos seus pedidos.
          </div>
        </div>
      </div>
    </div>
  );
}

function CampoCodigo({ valor, onChange }: { valor: string; onChange: (v: string) => void }) {
  return (
    <div>
      <label className="input-label" htmlFor="codigo">Código de verificação</label>
      <input
        id="codigo"
        type="text"
        inputMode="numeric"
        maxLength={6}
        value={valor}
        onChange={e => onChange(e.target.value.replace(/\D/g, '').slice(0, 6))}
        placeholder="000000"
        className="input-field text-center !text-2xl !font-bold !tracking-[0.5em] !py-3.5"
        autoComplete="one-time-code"
        required
      />
    </div>
  );
}

function Feedback({ erro, aviso }: { erro: string; aviso: string }) {
  return (
    <>
      {erro && (
        <div className="p-3.5 bg-red-50 border border-red-200 rounded-xl text-[13px] font-medium text-red-700 animate-fade-in">
          {erro}
        </div>
      )}
      {aviso && !erro && (
        <div className="p-3.5 bg-emerald-50 border border-emerald-200 rounded-xl text-[13px] font-medium text-emerald-700 animate-fade-in">
          {aviso}
        </div>
      )}
    </>
  );
}
